/* Common Imports */

import React from "react";
import { styled } from "@mui/system";

/* Component Imports */

import { Chip, Skeleton } from "@mui/material";

/* Styled Components */

const Container = styled("div")(({ theme }) => ({
	objectFit: "cover",
	width: "80%",
	borderRadius: "0.75rem",
	position: "relative",
}));

const Image = styled("img")(({ theme }) => ({
	display: "block",
	objectFit: "cover",
	height: "4rem",
	borderRadius: "0.75rem",
	width: "100%",
	/* XS breakpoint - 280.05 */
	[theme.breakpoints.up("xs")]: {
		height: "8rem",
	},
	/* iphone 5 - 320 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 39)]: {
		height: "5rem",
	},
	/* galaxy S8+ - 360 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 78)]: {
		height: "5.5rem",
	},
	/* iphone 12 pro and pixel - 390 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 108.95)]: {
		height: "5.75rem",
	},
	/* iphone XR - 414 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 130)]: {
		height: "6rem",
	},
	/* iphone 14 pro max - 430 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 149)]: {
		height: "6.25rem",
	},
	/* 457 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 176)]: {
		height: "6.75rem",
	},
	/* 485 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 204)]: {
		height: "7.25rem",
	},
	/* 512 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 231)]: {
		height: "7.5rem",
	},
	/* 540 */
	[theme.breakpoints.up("xsPlus")]: {
		height: "7.75rem",
	},
	/* 570  ~~3 cards start~~ */
	[theme.breakpoints.up(theme.breakpoints.values.xsPlus + 30)]: {
		height: "5.5rem",
	},
	/* 600  ~~2 cards start~~ */
	[theme.breakpoints.up("sm")]: {
		height: "7.75rem",
	},
	/* 642 ~~3 cards start~~ */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 42)]: {
		height: "6rem",
	},
	/* 684 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 84)]: {
		height: "6.25rem",
	},
	/* ipad Mini - 768 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 167)]: {
		height: "7rem",
	},
	/* ipad Air - 820 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 219)]: {
		height: "8rem",
	},
	/* 860 */
	[theme.breakpoints.up("sm860")]: {
		height: "8.25rem",
	},
	/* 910 */
	[theme.breakpoints.up("sm910")]: {
		height: "8.5rem",
	},
	/* 960 */
	[theme.breakpoints.up("md")]: {
		height: "8.75rem",
	},
	/* iPadPro - 1024 */
	[theme.breakpoints.up(theme.breakpoints.values.md + 64)]: {
		height: "9.25rem",
	},
	/* 1107 */
	[theme.breakpoints.up(theme.breakpoints.values.md + 147)]: {
		height: "9.5rem",
	},
	/* 1190 ~~Desktop Layout Start~~ */
	[theme.breakpoints.up("md1190")]: {
		height: "7rem",
	},
	/* 720p breakpoint */
	[theme.breakpoints.up("lg")]: {
		height: "7rem",
	},
	/* 1080p 125% breakpoint */
	[theme.breakpoints.up(theme.breakpoints.values.lg + 150)]: {
		height: "7rem",
	},
	/* 4k breakpoint 3840p */
	[theme.breakpoints.up(theme.breakpoints.values.xl + 1920)]: {
		height: "9rem",
	},
}));

const CustomChip = styled(Chip, {
	shouldForwardProp: (prop) => prop !== "transaction_type",
})<{ transaction_type: string }>(({ theme, transaction_type }) => ({
	"& .MuiChip-label": {
		padding: "0rem 0.5rem 0rem 0.5rem",
	},
	position: "absolute",
	top: 8,
	left: 8,
	height: "1.25rem",
	padding: "0rem",
	fontWeight: 500,
	fontSize: "0.65rem",
	letterSpacing: "0.02857em",
	borderRadius: "6px",
	backgroundColor:
		transaction_type === "buy"
			? "#b7efc5"
			: transaction_type === "rent"
				? "#ffc6ac"
				: transaction_type === "resale"
					? "#caf0f8"
					: "#b7efc5",
	color:
		transaction_type === "buy"
			? "#1e4620"
			: transaction_type === "rent"
				? "#4c2708"
				: transaction_type === "resale"
					? "#212121"
					: "#1e4620",
	/* iphone 5 - 320 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 39)]: {
		fontSize: "0.625rem",
	},
	/*iphone XR - 414*/
	[theme.breakpoints.up(theme.breakpoints.values.xs + 130)]: {
		fontSize: "0.7rem",
	},
	/* ipad Air - 820 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 219)]: {
		fontSize: "0.825rem",
	},
	/* 910 */
	[theme.breakpoints.up("sm910")]: {
		fontSize: "0.875rem",
	},
}));

/*

 & Setting a constant image for when the image is not available or something goes wrong

*/

const fallbackImage: string = "/images/uploads/property.jpg";

const GridImage = ({
	images,
	loading,
	transaction_type,
}: {
	images: string;
	loading: boolean;
	transaction_type: string;
}) => {
	/*

	& Check if page is loaded, if loaded set to true. if page loads we use regular image. if not we use lqip image.

	*/

	const [isPageLoaded, SetIsPageLoaded] = React.useState<boolean>(false);

	React.useEffect(() => {
		SetIsPageLoaded(true);
	}, [loading]);

	if (loading) {
		return (
			<React.Fragment>
				<Skeleton
					animation="wave"
					variant="rectangular"
					width="100%"
					height="100%"
					style={{ borderRadius: "16px", margin: "0rem" }}
				/>
			</React.Fragment>
		);
	}

	return (
		<React.Fragment>
			<Container>
				<Image
					src={
						images && images !== undefined && images !== "" ? (isPageLoaded ? images : fallbackImage) : fallbackImage
					}
					alt="Property Image"
					referrerPolicy="no-referrer"
					loading="eager"
					width="100%"
				/>
				<CustomChip
					label={
						transaction_type && transaction_type === "buy"
							? "Sale"
							: transaction_type.charAt(0).toUpperCase() + transaction_type.slice(1)
					}
					transaction_type={transaction_type}
				/>
			</Container>
		</React.Fragment>
	);
};

export default GridImage;
